<template>
    <v-app id="inspire" v-if="isLoaded">
        <v-app-bar app clipped-right color="primary" dark flat tabs>
            <v-app-bar-nav-icon @click.stop="drawer = !drawer" />
            <v-toolbar-title v-if="$route.meta.title">
                {{ $route.meta.title }}
            </v-toolbar-title>
            <v-toolbar-title v-else>
                <v-avatar v-if="business.profile_picture !== null" :tile="false" size="48">
                    <v-img :src="business.profile_picture" alt="aa" />
                </v-avatar>
                {{ business.name }}
            </v-toolbar-title>
            <v-progress-linear
                :indeterminate="true"
                absolute
                bottom
                rounded
                color="secondary"
                height="3"
                :active="isLoading"
                class="progress-bar"
                v-if="!$route.meta.toolbar"
            ></v-progress-linear>
            <v-spacer />
            <v-toolbar-items>
                <div class="d-flex align-center justify-center mr-4">
                    <v-btn x-large rounded icon @click="showBroadcastNotificationsModal()">
                        <v-icon>campaign</v-icon>
                    </v-btn>
                    <v-menu offset-y mx-0>
                        <template v-slot:activator="{ on }">
                            <v-btn x-large icon v-on="on">
                                <v-icon>person</v-icon>
                            </v-btn>
                        </template>
                        <v-list subheader dense>
                            <v-list-item @click="profileView">
                                <v-list-item-title>Profile</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="settingsView">
                                <v-list-item-title>Settings</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="logout">
                                <v-list-item-title>Log out</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
            </v-toolbar-items>
            <template v-slot:extension v-if="$route.meta.toolbar">
                <v-tabs v-model="tab" align-with-title color="" slider-color="secondary">
                    <v-tabs-slider color="primary" />
                    <v-tab :ripple="false" :to="`/businesses/${$route.params.id}/info`">Business info</v-tab>
                    <v-tab :ripple="false" :to="`/businesses/${$route.params.id}/menu`">Menu</v-tab>
                </v-tabs>
                <v-progress-linear :indeterminate="true" absolute bottom rounded color="secondary" height="3" :active="isLoading" class="progress-bar"></v-progress-linear>
            </template>
        </v-app-bar>
        <v-navigation-drawer v-model="drawer" app>
            <v-toolbar flat class="transparent">
                <v-list class="pa-0">
                    <v-list-item>
                        <v-list-item-avatar style="border: 0.5px solid #cccccc;">
                            <img src="../assets/images/logo.png" />
                        </v-list-item-avatar>
                        <v-list-item-content>
                            <v-list-item-title><span style="font-size: 20px;">Admin Panel</span></v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </v-toolbar>
            <v-list style="padding-top:0px;">
                <v-divider></v-divider>
                <template v-for="item in drawerItems">
                    <v-list-item :key="item.text" :to="item.routerLink" color="primary" :disabled="item.isEnabled === false">
                        <v-list-item-action>
                            <v-icon>{{ item.icon }}</v-icon>
                        </v-list-item-action>
                        <v-list-item-content>
                            <v-list-item-title>{{ item.text }}</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </template>
            </v-list>
        </v-navigation-drawer>
        <v-main>
            <slot></slot>
            <snackbar></snackbar>
        </v-main>
        <v-dialog v-model="broadcastNotificationsModal" persistent max-width="800px">
            <v-card>
                <v-card-title>
                    <span class="headline">Broadcast Notification</span>
                </v-card-title>
                <v-card-text>
                    <v-alert text border="left" dismissible :type="notificationBroadcastSuccess ? 'success' : 'error'" v-model="notificationBroadcastSubmitted">
                        <span v-html="notificationBroadcastCompletedMessage"></span>
                    </v-alert>
                    <v-form ref="notificationForm" v-model="notificationFormIsValid" lazy-validation>
                        <v-container class="mb-3">
                            <v-row>
                                <v-col cols="12" sm="6">
                                    <v-select
                                        v-model="notificationConfig.notification_type"
                                        :items="getNotificationTypes"
                                        label="Select notification type"
                                        item-text="key"
                                        item-value="id"
                                        color="primary"
                                        :rules="[v => !!v || 'Notification type is required']"
                                        required
                                    ></v-select>
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-text-field v-model="notificationConfig.cta_id" color="primary" label="Enter cta id (optional)" />
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="3" class="d-flex align-center justify-flex-start">
                                    <span class="body-1">Recipient type:</span>
                                </v-col>
                                <v-col cols="9">
                                    <v-radio-group v-model="notificationConfig.recipients_type" row @change="recipientsTypeChanged">
                                        <v-radio label="FCM Devices" value="fcm_devices"></v-radio>
                                        <v-radio label="Users" value="users"></v-radio>
                                    </v-radio-group>
                                </v-col>
                                <v-col cols="3" class="d-flex align-center justify-flex-start">
                                    <span class="body-1">Notify all?</span>
                                </v-col>
                                <v-col cols="9">
                                    <v-radio-group v-model="notificationConfig.notify_all" row>
                                        <v-radio label="Notify all" :value="true"></v-radio>
                                        <v-radio label="Choose recipients" :value="false" :disabled="notificationConfig.recipients_type === 'fcm_devices'"></v-radio>
                                    </v-radio-group>
                                </v-col>
                                <v-col cols="12">
                                    <v-autocomplete
                                        v-model="notificationConfig.recipient_ids"
                                        :items="getUsers"
                                        item-text="email"
                                        item-value="id"
                                        label="Select users"
                                        hint="Selected users will receive push notifications to all connected devices"
                                        persistent-hint
                                        multiple
                                        :disabled="notificationConfig.notify_all"
                                        :rules="[v => v.length > 0 || notificationConfig.notify_all || 'You must select at least one recipient']"
                                        chips
                                        clearable
                                        deletable-chips
                                        color="primary"
                                    ></v-autocomplete>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="#888888" text @click="closeBroadcastNotificationModal()">Close</v-btn>
                    <v-btn color="secondary" text @click="broadcastNotification()">Broadcast</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-app>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import bus from '../main';
import { Snackbar } from '@/components';
import { UsersApi, BusinessOptionsApi, NotificationsApi } from '@/api';
import { CLEAR_TOKEN, SET_ROLE } from '@/store/mutation-types';
import { SET_NOTIFICATION_TYPES } from '@/store/modules/notifications/mutation-types';
import { SET_USERS, SET_ROLES } from '@/store/modules/users/mutation-types';
import { dates, fonts, filters } from '@/utils/mixins';

export default {
    name: 'app',
    components: { Snackbar },
    mixins: [dates, fonts, filters],
    data: () => ({
        broadcastNotificationsModal: false,
        notificationFormIsValid: false,
        notificationConfig: {
            notification_type: null,
            cta_id: null,
            recipients_type: 'fcm_devices',
            notify_all: true,
            recipient_ids: [],
        },
        notificationBroadcastSubmitted: false,
        notificationBroadcastSuccess: false,
        notificationBroadcastCompletedMessage: null,
        tabs: false,
        tab: null,
        business: {},
        role_name: null,
        userObject: null,
        organizationObject: null,
        drawer: null,
        language: null,
        drawerItems: [
            {
                isActive: false,
                isEnabled: true,
                icon: 'store',
                text: 'Businesses',
                routerLink: '/',
            },
            {
                isActive: false,
                isEnabled: true,
                icon: 'group',
                text: 'User Management',
                routerLink: '/users',
            },
            {
                isActive: false,
                isEnabled: true,
                icon: 'label',
                text: 'Tags Management',
                routerLink: '/tags',
            },
            {
                isActive: false,
                isEnabled: true,
                icon: 'schedule',
                text: 'Activity Logs',
                routerLink: '/activity-logs',
            },
            {
                isActive: false,
                isEnabled: true,
                icon: 'work',
                text: 'Proposals',
                routerLink: '/proposals',
            },
            {
                isActive: false,
                isEnabled: true,
                icon: 'where_to_vote',
                text: 'Suggestions',
                routerLink: '/suggestions',
            },
        ],
        isLoaded: false,
    }),
    computed: {
        ...mapGetters(['isLoading']),
        ...mapGetters('Notifications', ['getNotificationTypes']),
        ...mapGetters('Users', ['getUsers']),
    },
    async created() {
        await this.loadUserRoles();
        await this.loadBusinessOptions();
        await this.loadNotificationsTypes();
        await this.loadUsers();
        await this.loadData();
        this.initEvents();
    },
    methods: {
        async logout() {
            await UsersApi.logout();
            this.$store.commit(CLEAR_TOKEN);
            this.$router.push({ name: 'login' });
        },
        async loadData() {
            this.$store.commit(SET_ROLE, { role: 'admin' });
            this.role_name = 'admin';
            // const { data: userObject } = await UsersApi.me();
            // this.userObject = userObject;
            // if (this.userObject.is_superuser) {
            //     this.$store.commit(SET_ROLE, { role: 'admin' });
            //     this.role_name = 'admin';
            // } else {
            //     const { role } = userObject.businesses[0];
            //     this.role_name = userObject.businesses[0].role_name;
            //     const { business } = userObject.businesses[0];
            //     this.$store.commit(SET_ROLE, { role });
            //     this.$store.commit(SET_BUSINESS, { business });
            // }
            this.isLoaded = true;
        },
        ...mapMutations('Users', {
            setRoles: SET_ROLES,
            setUsers: SET_USERS,
        }),
        ...mapMutations('BusinessOptions', {
            setTagsMutation: 'setTagsMutation',
            setServiceOptionsMutation: 'setServiceOptionsMutation',
            setFacilityOptionsMutation: 'setFacilityOptionsMutation',
            setParkingOptionsMutation: 'setParkingOptionsMutation',
            setGoodForOptionsMutation: 'setGoodForOptionsMutation',
            setMealOptionsMutation: 'setMealOptionsMutation',
            setFoodOptionsMutation: 'setFoodOptionsMutation',
        }),
        ...mapMutations('Notifications', {
            setNotificationTypes: SET_NOTIFICATION_TYPES,
        }),
        async loadUserRoles() {
            try {
                const { data: roles } = await UsersApi.roles();
                this.setRoles(roles);
            } catch (error) {
                this.$store.commit(CLEAR_TOKEN);
                this.$router.push({ name: 'login' });
            }
        },
        async loadBusinessOptions() {
            const { data: tags } = await BusinessOptionsApi.getTags();
            const { data: serviceOptions } = await BusinessOptionsApi.serviceOptions();
            const { data: facilityOptions } = await BusinessOptionsApi.facilityOptions();
            const { data: parkingOptions } = await BusinessOptionsApi.parkingOptions();
            const { data: goodForOptions } = await BusinessOptionsApi.goodForOptions();
            const { data: mealOptions } = await BusinessOptionsApi.mealOptions();
            const { data: foodOptions } = await BusinessOptionsApi.foodOptions();

            this.setTagsMutation(tags);
            this.setServiceOptionsMutation(serviceOptions);
            this.setFacilityOptionsMutation(facilityOptions);
            this.setParkingOptionsMutation(parkingOptions);
            this.setGoodForOptionsMutation(goodForOptions);
            this.setMealOptionsMutation(mealOptions);
            this.setFoodOptionsMutation(foodOptions);
        },
        async loadNotificationsTypes() {
            const { data } = await NotificationsApi.notificationTypesList();
            this.setNotificationTypes(data);
        },
        async loadUsers() {
            const { data } = await UsersApi.all({ limit: 1000 });
            this.setUsers(data);
        },
        async broadcastNotification() {
            this.notificationBroadcastSubmitted = false;
            if (!this.$refs.notificationForm.validate()) {
                return;
            }
            if (this.notificationConfig.notify_all) {
                this.notificationConfig.recipient_ids = [];
            }
            try {
                const { data } = await NotificationsApi.broadcastNotification(this.notificationConfig);
                this.notificationBroadcastSubmitted = true;
                this.notificationBroadcastSuccess = true;
                this.notificationBroadcastCompletedMessage = `Broadcast completed! Results: <br/><ul><li>Success: ${data['success']}</li><li>Failure: ${data['failure']}</li></ul>`;
            } catch (error) {
                if (error.response !== undefined && error.response.data !== undefined) {
                    this.notificationBroadcastCompletedMessage = error.response.data;
                } else {
                    this.notificationBroadcastCompletedMessage = 'There was an error while trying to broadcast notification';
                }
                this.notificationBroadcastSubmitted = true;
                this.notificationBroadcastSuccess = false;
            }
        },
        closeBroadcastNotificationModal() {
            this.broadcastNotificationsModal = false;
            this.$refs.notificationForm.reset();
            this.$refs.notificationForm.resetValidation();
        },
        recipientsTypeChanged(value) {
            if (value === 'fcm_devices') {
                this.notificationConfig.notify_all = true;
            }
        },
        initEvents() {
            bus.$off('load-business-event');
            bus.$on('load-business-event', business => {
                this.business = business;
            });
        },
        profileView() {
            this.$router.push({ name: 'profile' });
        },
        settingsView() {
            this.$router.push({ name: 'settings' });
        },
        showBroadcastNotificationsModal() {
            this.notificationConfig = {
                notification_type: null,
                cta_id: null,
                recipients_type: 'fcm_devices',
                notify_all: true,
                recipient_ids: [],
            };
            this.broadcastNotificationsModal = true;
        },
    },
};
</script>

<style lang="scss">
.progress-bar {
    margin: -2px !important;
}
</style>
